'use client';

import { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useAuth } from '../../hooks/use-auth';

export function CommentMintingLoader({
  user,
  isOpen,
  onClose,
  commentContent,
  transactionHash,
  error = null,
  onRetry = null,
  status = 'minting',
}) {
  const currentUser = user;

  // Format the comment preview (truncate if too long)
  const formatCommentPreview = (comment) => {
    if (!comment) return '';
    return comment.length > 100 ? `${comment.substring(0, 100)}...` : comment;
  };

  // Format the transaction hash
  const formatTransactionHash = (hash) => {
    if (!hash) return 'Processing...';
    return `${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`;
  };

  // Handle retry
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900/95 backdrop-blur-xl border-white/10 text-white">
        <div className="flex flex-col items-center p-4">
          {/* Comment Preview */}
          <div className="bg-black/40 rounded-xl p-4 mb-6 w-full border border-white/5">
            <div className="flex items-start gap-3">
              <Avatar className="h-8 w-8 border border-white/10">
                <AvatarImage
                  src={currentUser?.avatar || '/placeholder.svg'}
                  alt={currentUser?.name || 'User'}
                />
                <AvatarFallback className="bg-zinc-800 text-zinc-400">
                  {currentUser?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-white">
                    {currentUser?.name || 'Anonymous'}
                  </span>
                  <Badge
                    variant="outline"
                    className="text-xs border-violet-500/30 text-violet-400 bg-violet-500/10"
                  >
                    NFT Comment
                  </Badge>
                </div>
                <p className="text-sm text-zinc-400 whitespace-pre-wrap">
                  {formatCommentPreview(commentContent)}
                </p>
              </div>
            </div>
          </div>

          {/* Status Indicator */}
          <div className="mb-6">
            {status === 'minting' && (
              <div className="relative">
                {/* Outer spinning ring */}
                <div className="w-24 h-24 rounded-full border-4 border-t-violet-500 border-r-violet-500/30 border-b-violet-500/10 border-l-violet-500/60 animate-spin"></div>

                {/* Inner content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-violet-500"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
              </div>
            )}

            {status === 'success' && (
              <div className="w-24 h-24 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-emerald-500"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
            )}

            {status === 'error' && (
              <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-red-500"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
            )}
          </div>

          {/* Status Text */}
          <h2 className="text-xl font-bold mb-2 text-center text-white">
            {status === 'minting' && 'Minting Your Comment'}
            {status === 'success' && 'Comment Minted Successfully!'}
            {status === 'error' && 'Minting Failed'}
          </h2>

          <p className="text-zinc-400 text-center mb-6">
            {status === 'minting' &&
              'Please wait while your comment is being minted as an NFT...'}
            {status === 'success' &&
              'Your comment has been successfully minted as an NFT on the blockchain.'}
            {status === 'error' &&
              (error ||
                'There was an error minting your comment. Please try again.')}
          </p>

          {/* Action Buttons */}
          {status === 'error' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRetry}
                className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white"
              >
                Try Again
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
